import React, { useEffect, useState, useRef } from 'react';
import ComplimentPic from "./compliment.webp";
import Swal from 'sweetalert2';

const SURVEYOR_API = "https://portal.lsb.go.ke/account/surveyors/";
const COMPLIMENT_API = "https://portal.lsb.go.ke/lsb/compliments/";

function Compliments() {
    const [surveyors, setSurveyors] = useState([]);
    const [loadingSurveyors, setLoadingSurveyors] = useState(true);
    const [surveyorsError, setSurveyorsError] = useState(null);

    // Fields for the compliment form
    const [yourName, setYourName] = useState('');
    const [subject, setSubject] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [county, setCounty] = useState('');
    const [subCounty, setSubCounty] = useState('');
    const [message, setMessage] = useState('');

    // Surveyor search and select
    const [selectedSurveyor, setSelectedSurveyor] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [dropdownOpen, setDropdownOpen] = useState(false);

    // Submission state
    const [submitting, setSubmitting] = useState(false);

    const dropdownRef = useRef(null);

    useEffect(() => {
        let isMounted = true;
        setLoadingSurveyors(true);
        setSurveyorsError(null);

        function normalizeList(data) {
            let list =
                (Array.isArray(data) && data) ||
                (Array.isArray(data?.results) && data.results) ||
                [];
            return list.map(s => ({
                ...s,
                id: s.license_no,
                license_number: s.license_no,
                name: s.name,
                license_no: s.license_no,
            }));
        }

        async function fetchAllSurveyors() {
            let allSurveyors = [];
            let url = SURVEYOR_API;
            let pageLimit = 100;
            let pageCount = 0;
            try {
                while (url && pageCount < pageLimit) {
                    const res = await fetch(url);
                    if (!res.ok) throw new Error("Failed to load surveyors");
                    const data = await res.json();
                    allSurveyors = allSurveyors.concat(normalizeList(data));
                    url = data.next;
                    pageCount++;
                }
                if (isMounted) {
                    setSurveyors(allSurveyors);
                    setLoadingSurveyors(false);
                }
            } catch (e) {
                if (isMounted) {
                    setSurveyorsError("Unable to fetch surveyors.");
                    setLoadingSurveyors(false);
                }
            }
        }

        fetchAllSurveyors();

        return () => {
            isMounted = false;
        };
    }, []);

    // Close dropdown on outside click
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        }
        if (dropdownOpen) {
            window.addEventListener('mousedown', handleClickOutside);
        } else {
            window.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            window.removeEventListener('mousedown', handleClickOutside);
        }
    }, [dropdownOpen]);

    // Only show dropdown if something is being searched for, not for empty input
    const matchingSurveyors = searchTerm
        ? surveyors.filter(s =>
            s.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : [];

    // Find the selected surveyor object by name
    const selectedSurveyorObj = selectedSurveyor
        ? (typeof selectedSurveyor === 'object'
            ? selectedSurveyor
            : surveyors.find(s => s.name === selectedSurveyor))
        : null;

    // Form validation
    const isFormValid = () => {
        return (
            yourName.trim() &&
            subject.trim() &&
            email.trim() &&
            /^[^ ]+@[^ ]+\.[^ ]+$/.test(email.trim()) &&
            phoneNumber.trim() &&
            county.trim() &&
            subCounty.trim() &&
            message.trim() &&
            selectedSurveyorObj && selectedSurveyorObj.license_no
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isFormValid()) {
            Swal.fire({
                icon: 'error',
                title: 'Form Error',
                text: 'Please fill all required fields correctly.',
            });
            return;
        }

        setSubmitting(true);

        const payload = {
            name: yourName.trim(),
            email: email.trim(),
            phone_number: phoneNumber.trim(),
            county: county.trim(),
            sub_county: subCounty.trim(),
            subject: subject.trim(),
            message: message.trim(),
            license_no: selectedSurveyorObj.license_no
        };

        try {
            const res = await fetch(COMPLIMENT_API, {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                const errMsg = (await res.json())?.detail || 'Failed to submit compliment.';
                throw new Error(errMsg);
            }
            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Thank you for your compliment. It has been submitted!',
            });
            // Reset form
            setYourName('');
            setSubject('');
            setEmail('');
            setPhoneNumber('');
            setCounty('');
            setSubCounty('');
            setMessage('');
            setSelectedSurveyor(null);
            setSearchTerm('');
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || "An error occurred while submitting.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Enhanced form field info
    const fieldInfos = {
        your_full_name: "Please enter your full name as it helps us personalize your compliment.",
        subject: "Briefly explain the subject of your compliment.",
        phone_number: "A valid phone number lets us follow up if needed.",
        county: "Select the county where the compliment applies.",
        sub_county: "Specify the sub-county for a more precise location.",
        message: "Write your compliment in detail here!",
        surveyor_search: "Begin typing the name of a licensed surveyor to select them."
    };
    const [focusedField, setFocusedField] = useState('');

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="relative mb-12">
                <img
                    className="h-96 w-full object-cover object-center rounded-b-2xl shadow-2xl"
                    src={ComplimentPic}
                    alt="A beautiful landscape"
                    loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-b-2xl"></div>
                <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-10 flex flex-col items-center w-full px-4">
                    <h1
                        className="text-4xl md:text-5xl font-extrabold text-white mb-3 text-center tracking-tight shadow-sm"
                    >
                        Applaud Good Work
                    </h1>
                    <span
                        className="px-6 py-3 rounded-full shadow-xl text-lg font-semibold bg-[#3d0707] text-white transform hover:scale-105 transition-transform duration-300"
                    >
                        Celebrating Professionalism & Service
                    </span>
                </div>
            </div>

            <div className="container mx-auto p-8 md:p-12 mb-8">
                <div className="mb-8 text-center">
                    <p className="text-lg text-gray-700">
                        We would love to hear about the outstanding professionals who uphold the standards of the board, offer exemplary services to the public, and sustain the good name of our board.
                    </p>
                </div>

                <form className="space-y-6" onSubmit={handleSubmit} autoComplete="off">
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="relative group">
                            <label htmlFor="your-full-name" className="block text-sm font-medium text-gray-700 mb-1">
                                Your Full Name
                            </label>
                            <input
                                type="text"
                                id="your-full-name"
                                className="w-full px-4 py-3 bg-gray-50 rounded-lg border-gray-300 focus:ring-2 focus:ring-[#3d0707] focus:border-transparent transition-all ease-in-out"
                                name="your_full_name"
                                placeholder="e.g. Jane Doe"
                                value={yourName}
                                onChange={e => setYourName(e.target.value)}
                                onFocus={() => setFocusedField('your_full_name')}
                                onBlur={() => setFocusedField('')}
                                required
                            />
                            {focusedField === 'your_full_name' && (
                                <div className="text-xs text-gray-500 mt-1 pl-1">{fieldInfos.your_full_name}</div>
                            )}
                        </div>

                        <div className="relative group">
                            <label htmlFor="your-email" className="block text-sm font-medium text-gray-700 mb-1">
                                Email
                            </label>
                            <input
                                type="email"
                                id="your-email"
                                className="w-full px-4 py-3 bg-gray-50 rounded-lg border-gray-300 focus:ring-2 focus:ring-[#3d0707] focus:border-transparent transition-all"
                                name="email"
                                placeholder="e.g. jane@email.com"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                onFocus={() => setFocusedField('email')}
                                onBlur={() => setFocusedField('')}
                                required
                            />
                            {focusedField === 'email' && (
                                <div className="text-xs text-gray-500 mt-1 pl-1">We'll never share your email.</div>
                            )}
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="relative group">
                            <label htmlFor="your-phone" className="block text-sm font-medium text-gray-700 mb-1">
                                Phone Number
                            </label>
                            <input
                                type="text"
                                id="your-phone"
                                className="w-full px-4 py-3 bg-gray-50 rounded-lg border-gray-300 focus:ring-2 focus:ring-[#3d0707] focus:border-transparent transition-all"
                                name="phone_number"
                                placeholder="e.g. 0712 345 678"
                                value={phoneNumber}
                                onChange={e => setPhoneNumber(e.target.value)}
                                onFocus={() => setFocusedField('phone_number')}
                                onBlur={() => setFocusedField('')}
                                required
                            />
                            {focusedField === 'phone_number' && (
                                <div className="text-xs text-gray-500 mt-1 pl-1">{fieldInfos.phone_number}</div>
                            )}
                        </div>

                        <div className="relative group">
                            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                                Subject
                            </label>
                            <input
                                type="text"
                                id="subject"
                                className="w-full px-4 py-3 bg-gray-50 rounded-lg border-gray-300 focus:ring-2 focus:ring-[#3d0707] focus:border-transparent transition-all"
                                name="subject"
                                placeholder="e.g. Outstanding Service"
                                value={subject}
                                onChange={e => setSubject(e.target.value)}
                                onFocus={() => setFocusedField('subject')}
                                onBlur={() => setFocusedField('')}
                                required
                            />
                            {focusedField === 'subject' && (
                                <div className="text-xs text-gray-500 mt-1 pl-1">{fieldInfos.subject}</div>
                            )}
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="relative group">
                            <label htmlFor="county" className="block text-sm font-medium text-gray-700 mb-1">
                                County
                            </label>
                            <input
                                type="text"
                                id="county"
                                className="w-full px-4 py-3 bg-gray-50 rounded-lg border-gray-300 focus:ring-2 focus:ring-[#3d0707] focus:border-transparent transition-all"
                                name="county"
                                placeholder="e.g. Nairobi"
                                value={county}
                                onChange={e => setCounty(e.target.value)}
                                onFocus={() => setFocusedField('county')}
                                onBlur={() => setFocusedField('')}
                                required
                            />
                            {focusedField === 'county' && (
                                <div className="text-xs text-gray-500 mt-1 pl-1">{fieldInfos.county}</div>
                            )}
                        </div>

                        <div className="relative group">
                            <label htmlFor="sub-county" className="block text-sm font-medium text-gray-700 mb-1">
                                Sub-County
                            </label>
                            <input
                                type="text"
                                id="sub-county"
                                className="w-full px-4 py-3 bg-gray-50 rounded-lg border-gray-300 focus:ring-2 focus:ring-[#3d0707] focus:border-transparent transition-all"
                                name="sub_county"
                                placeholder="e.g. Westlands"
                                value={subCounty}
                                onChange={e => setSubCounty(e.target.value)}
                                onFocus={() => setFocusedField('sub_county')}
                                onBlur={() => setFocusedField('')}
                                required
                            />
                            {focusedField === 'sub_county' && (
                                <div className="text-xs text-gray-500 mt-1 pl-1">{fieldInfos.sub_county}</div>
                            )}
                        </div>
                    </div>

                    <div className="relative" ref={dropdownRef}>
                        <label htmlFor="surveyor-search" className="block text-sm font-medium text-gray-700 mb-1">
                            Surveyor Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            id="surveyor-search"
                            className="w-full px-4 py-3 bg-gray-50 rounded-lg border-gray-300 focus:ring-2 focus:ring-[#3d0707] focus:border-transparent transition-all"
                            name="surveyor_search"
                            placeholder="Begin typing the surveyor's name"
                            autoComplete="off"
                            value={searchTerm}
                            onChange={e => {
                                setSearchTerm(e.target.value);
                                setSelectedSurveyor(null);
                                setDropdownOpen(!!e.target.value);
                            }}
                            onFocus={() => {
                                setFocusedField('surveyor_search');
                                if (searchTerm) setDropdownOpen(true);
                            }}
                            onBlur={() => setFocusedField('')}
                            required
                            style={
                                searchTerm && !selectedSurveyorObj
                                    ? { borderColor: "red" }
                                    : undefined
                            }
                        />
                        {focusedField === 'surveyor_search' && (
                            <div className="text-xs text-gray-500 mt-1 pl-1">{fieldInfos.surveyor_search}</div>
                        )}

                        {loadingSurveyors && (
                            <div className="text-sm text-gray-500 mt-2">Loading surveyors...</div>
                        )}
                        {surveyorsError && (
                            <div className="text-red-500 text-sm mt-2">{surveyorsError}</div>
                        )}
                        {dropdownOpen && searchTerm && (
                            <div
                                className="absolute left-0 right-0 mt-1 w-full bg-white rounded-lg shadow-lg max-h-60 overflow-auto z-50 animate-fadeIn"
                            >
                                {matchingSurveyors.length > 0 ? (
                                    matchingSurveyors.slice(0, 10).map(surveyor => (
                                        <div
                                            key={surveyor.id}
                                            className="cursor-pointer px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-[#ebcab3]/30 transition-colors"
                                            onMouseDown={() => {
                                                setSelectedSurveyor(surveyor);
                                                setSearchTerm(surveyor.name);
                                                setDropdownOpen(false);
                                            }}
                                        >
                                            <p className="font-semibold">{surveyor.name}</p>
                                            <p className="text-xs text-gray-500">License #{surveyor.license_no}</p>
                                        </div>
                                    ))
                                ) : (
                                    <div className="px-4 py-3 text-sm text-gray-500">No surveyors found.</div>
                                )}
                                {matchingSurveyors.length > 10 && (
                                    <div className="px-4 py-2 text-xs text-gray-400 bg-gray-50">More results, please narrow your search.</div>
                                )}
                            </div>
                        )}
                        {selectedSurveyorObj && (
                            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between animate-fadeIn">
                                <div>
                                    <p className="font-semibold text-green-800">{selectedSurveyorObj.name}</p>
                                    <p className="text-sm text-green-600">License No: {selectedSurveyorObj.license_no}</p>
                                </div>
                                <span className="text-2xl text-green-500">✓</span>
                            </div>
                        )}
                    </div>

                    <div className="relative group">
                        <label htmlFor="nature-of-work" className="block text-sm font-medium text-gray-700 mb-1">
                            Compliment Message
                        </label>
                        <textarea
                            className="w-full px-4 py-3 bg-gray-50 rounded-lg border-gray-300 focus:ring-2 focus:ring-[#3d0707] focus:border-transparent transition-all appearance-none"
                            id="nature-of-work"
                            placeholder="Write your compliment message here..."
                            name="message"
                            rows="6"
                            value={message}
                            onChange={e => setMessage(e.target.value)}
                            onFocus={() => setFocusedField('message')}
                            onBlur={() => setFocusedField('')}
                            required
                        />
                        {focusedField === 'message' && (
                            <div className="text-xs text-gray-500 mt-1 pl-1">{fieldInfos.message}</div>
                        )}
                    </div>

                    <div className="pt-4 flex justify-center">
                        <button
                            type="submit"
                            disabled={submitting || !isFormValid()}
                            className="w-full md:w-auto px-8 py-2 rounded-lg font-bold text-lg text-white bg-[#3d0707] hover:bg-[#5a1a1a] focus:outline-none focus:ring-4 focus:ring-[#3d0707]/50 transition-all duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center shadow-lg transform hover:scale-105"
                        >
                            {submitting ? (
                                <>
                                    <svg className="animate-spin h-5 w-5 mr-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                                    </svg>
                                    Submitting...
                                </>
                            ) : (
                                <>
                                    <svg className="h-6 w-6 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                    Submit Compliment
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default Compliments;
